/**
 * Product Management React Query Hooks
 * 
 * Provides React Query hooks for product catalog management including:
 * - Product CRUD operations
 * - Product categories and metadata
 * - Product filtering and search
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useProducts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches products with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering products
 * @param {string} [filters.category] - Filter by product category
 * @param {string} [filters.status] - Filter by product status
 * @param {string} [filters.search] - Search by name or SKU
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.limit] - Items per page
 * @returns {Object} React Query result with products data
 * @property {Object} data - Products response object
 * @property {Array} data.items - Array of product records
 * @property {number} data.total - Total number of products
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useProducts = (filters = {}) => {
  return useQuery({
    queryKey: ['products', filters],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.PRODUCTS || '/api/v1/products', { params: filters });
      const data = response?.data ?? {};
      // API returns array directly or wrapped in items/data/products
      const items = Array.isArray(data) ? data :
                   Array.isArray(data?.items) ? data.items : 
                   Array.isArray(data?.data) ? data.data :
                   Array.isArray(data?.products) ? data.products : [];
      const total = data?.pagination?.total || data?.meta?.total || data?.total || items.length;
      return { items, total, products: items, raw: data };
    },
  });
};

/**
 * Fetches a single product by ID
 * 
 * Query is disabled if productId is not provided
 * 
 * @param {string} productId - Product ID
 * @returns {Object} React Query result with product data
 * @property {Object} data - Product record with full details
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useProduct = (productId) => {
  return useQuery({
    queryKey: ['products', productId],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.PRODUCTS}/${productId}`);
      return response.data;
    },
    enabled: !!productId,
  });
};

/**
 * Mutation hook to create a new product
 * 
 * Automatically invalidates products query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createProduct = useCreateProduct();
 * createProduct.mutate({ 
 *   name: 'Widget Pro', 
 *   sku: 'WDG-001',
 *   category: 'Electronics',
 *   price: 99.99 
 * });
 */
export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(API_ENDPOINTS.PRODUCTS, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Mutation hook to update an existing product
 * 
 * Automatically invalidates both the specific product and products list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateProduct = useUpdateProduct();
 * updateProduct.mutate({ 
 *   productId: '123', 
 *   data: { price: 89.99, status: 'active' } 
 * });
 */
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ productId, data }) => {
      const response = await api.put(`${API_ENDPOINTS.PRODUCTS}/${productId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Mutation hook to delete a product
 * 
 * Automatically invalidates products query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteProduct = useDeleteProduct();
 * deleteProduct.mutate('product_123');
 */
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (productId) => {
      const response = await api.delete(`${API_ENDPOINTS.PRODUCTS}/${productId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Fetches product categories
 * 
 * @returns {Object} React Query result with categories data
 * @property {Array} data - Array of product category records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useProductCategories = () => {
  return useQuery({
    queryKey: ['products', 'categories'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/categories`);
      const data = response.data;
      return { categories: Array.isArray(data?.categories) ? data.categories : (Array.isArray(data) ? data : []) };
    },
  });
};

/**
 * Fetches product metadata and configuration
 * 
 * @returns {Object} React Query result with product metadata
 * @property {Object} data - Product metadata object
 * @property {Array} data.attributes - Available product attributes
 * @property {Array} data.units - Available measurement units
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useProductMeta = () => {
  return useQuery({
    queryKey: ['products', 'meta'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.PRODUCTS}/meta`);
      return response.data;
    },
  });
};

/**
 * Hook to get product by barcode
 * @param {string} barcode - Product barcode
 * @returns {Object} Query result with product data
 */
export const useProductByBarcode = (barcode) => {
  return useQuery({
    queryKey: ['products', 'barcode', barcode],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.PRODUCTS}/barcode/${barcode}`);
      return data;
    },
    enabled: !!barcode,
  });
};

/**
 * Hook to get manager products
 * @returns {Object} Query result with manager products data
 */
export const useManagerProducts = () => {
  return useQuery({
    queryKey: ['products', 'manager'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.PRODUCTS}/manager`);
      return response.data;
    },
  });
};

/**
 * Hook to create manager product
 * @returns {Object} Mutation object for creating manager products
 */
export const useCreateManagerProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (productData) => {
      const response = await api.post(`${API_ENDPOINTS.PRODUCTS}/manager`, productData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Hook to update manager product
 * @returns {Object} Mutation object for updating manager products
 */
export const useUpdateManagerProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ productId, productData }) => {
      const response = await api.put(`${API_ENDPOINTS.PRODUCTS}/manager/${productId}`, productData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Hook to delete manager product
 * @returns {Object} Mutation object for deleting manager products
 */
export const useDeleteManagerProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (productId) => {
      const response = await api.delete(`${API_ENDPOINTS.PRODUCTS}/manager/${productId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Fetches product types
 * 
 * @returns {Object} React Query result with product types data
 * @property {Array} data.types - Array of product type records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useProductTypes = () => {
  return useQuery({
    queryKey: ['products', 'types'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.PRODUCTS}/types`);
      return response.data;
    },
  });
};

/**
 * Mutation hook to import products from CSV file
 * 
 * Automatically invalidates products query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const importProducts = useImportProducts();
 * const formData = new FormData();
 * formData.append('file', file);
 * importProducts.mutate(formData);
 */
export const useImportProducts = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (formData) => {
      const response = await api.post(`${API_ENDPOINTS.INVENTORY}/products/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

/**
 * Fetch product statistics
 * @returns {Object} Query result with product stats
 */
export const useProductStats = () => {
  return useQuery({
    queryKey: ['products', 'stats'],
    queryFn: async () => {
      // Use the products/stats endpoint
      try {
        const response = await api.get(`${API_ENDPOINTS.PRODUCTS}/stats`);
        const data = response.data || {};
        return {
          totalProducts: data.total_products || 0,
          activeProducts: data.active_products || 0,
          virtualProducts: data.virtual_products || 0,
          categories: data.top_categories || 0,
          lowStock: data.low_stock || 0,
          pendingValidation: data.pending_validation || 0,
          totalValue: data.total_value || 0,
          avgMargin: data.avg_margin || 0,
          totalProductsChange: data.total_products_change || 0,
          activeProductsChange: data.active_products_change || 0,
          totalValueChange: data.total_value_change || 0
        };
      } catch {
        // Fallback to empty stats on error
        return {
          totalProducts: 0,
          activeProducts: 0,
          virtualProducts: 0,
          categories: 0,
          lowStock: 0,
          pendingValidation: 0,
          totalValue: 0,
          avgMargin: 0,
          totalProductsChange: 0,
          activeProductsChange: 0,
          totalValueChange: 0
        };
      }
    },
  });
};

/**
 * Fetch product categories from analytics endpoint
 * @returns {Object} Query result with categories
 */
export const useProductCategoriesAnalytics = () => {
  return useQuery({
    queryKey: ['products', 'categories', 'analytics'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.ANALYTICS}/products/categories`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (Array.isArray(data.categories) ? data.categories : []);
    },
  });
};

/**
 * Fetches inventory statuses for product management
 * 
 * @returns {Object} React Query result with inventory statuses
 * @property {Array} data.statuses - Array of inventory status options
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useInventoryStatuses = () => {
  return useQuery({
    queryKey: ['inventory', 'statuses'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/statuses`);
      const data = response.data;
      return { statuses: Array.isArray(data?.statuses) ? data.statuses : (Array.isArray(data) ? data : []) };
    },
  });
};
